import { useToast } from '@/lib/hooks/use-toast';
import { useAuth } from '@/lib/hooks/useAuth';
import { Profile, User } from '@/lib/models/client.types';
import { userService } from '@/lib/services/user.service';
import { profileActions, profileSelectors } from '@/lib/store/profile.store';
import { userActions, userSelectors } from '@/lib/store/user.store';
import { logger } from '@/lib/utils/logger';
import { observer } from '@legendapp/state/react';
import { signOut } from 'next-auth/react';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type UserFormDataType = {
  name: string;
  email: string;
  bio: string;
  expertiseAreas: string[];
  city: string;
  language: string;
  ageGroup: string;
  userType: string;
};

export type ProfileFormDataType = Profile;

// Context interface
interface UserContextType {
  // State
  user: User | null;
  isLoading: boolean;
  error: Error | null;

  // Actions
  fetchUser: () => Promise<void>;
  updateUser: (data: Partial<User>) => Promise<void>;
  updateLanguage: (language: string) => Promise<void>;
  deleteAccount: () => Promise<void>;

  // Helpers
  hasUserChanges: (updatedData: Partial<User>) => boolean;

  // Form data
  userFormData: UserFormDataType;
  setUserFormData: (data: UserFormDataType) => void;

  // Profile
  profile: Profile | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  profileFormData: ProfileFormDataType;
  setProfileFormData: (data: ProfileFormDataType) => void;

  // Helpers
  hasProfileChanges: (updatedData: Partial<Profile>) => boolean;
}

// Create context with a default undefined value
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider props
interface UserProviderProps {
  children: ReactNode;
}

// Provider component - Using observer pattern from Legend State
export const UserProvider = observer(({ children }: UserProviderProps) => {
  const { session } = useAuth();
  const { show, success, error: showError } = useToast();
  const userFetchedRef = useRef(false);
  const profileFetchedRef = useRef(false);
  const [userFormData, setUserFormData] = useState<UserFormDataType>({
    name: '',
    email: '',
    bio: '',
    expertiseAreas: [] as string[],
    city: '',
    language: 'en',
    ageGroup: '',
    userType: '',
  });
  const [profileFormData, setProfileFormData] = useState<ProfileFormDataType>({
    biography: '',
    areasOfLaw: [],
    specialization: [],
    fullAddress: '',
    city: '',
    stateProvince: '',
    country: '',
    languages: [],
    education: '',
    profilePicture: '',
  });

  // Get values from selectors - these are now reactive with observer HOC
  const user = userSelectors.getUser();
  const isLoading = userSelectors.isLoading();
  const errorState = userSelectors.getError();

  const profile = profileSelectors.getProfile();

  // Set session in store when it changes
  useEffect(() => {
    userActions.setSession(session);

    // Fetch user when session becomes available
    if (session?.user?.id) {
      if (!user && !userFetchedRef.current) {
        userFetchedRef.current = true;
        userActions.fetchUser();
      }
      if (!profile && !profileFetchedRef.current) {
        profileFetchedRef.current = true;
        profileActions.fetchProfile();
      }
    }
  }, [session, user, profile]);

  // Reset refs when session changes
  useEffect(() => {
    if (!session?.user?.id) {
      userFetchedRef.current = false;
      profileFetchedRef.current = false;
    }
  }, [session]);

  // Account deletion function
  const deleteAccount = async () => {
    if (!session?.user?.id) return;

    try {
      show({
        title: 'Deleting account...',
        description: 'Please wait while we process your request.',
      });

      const response = await userService.deleteAccount(
        session.user.id,
        session
      );

      if (response) {
        success({
          title: 'Account deleted',
          description: 'Your account has been successfully deleted.',
        });

        await signOut({ callbackUrl: '/' });
      }
    } catch (e) {
      logger.error('Error deleting account:', e);
      showError({
        title: 'Error',
        description: 'Failed to delete account',
      });
    }
  };

  // Helper to check if user has changes
  const hasUserChanges = (updatedData: Partial<User>): boolean => {
    if (!user) return false;

    return Object.keys(updatedData).some((key) => {
      const k = key as keyof User;
      return updatedData[k] !== user[k];
    });
  };

  // Helper to check if profile has changes
  const hasProfileChanges = (updatedData: Partial<Profile>): boolean => {
    if (!profile) return false;

    return Object.keys(updatedData).some((key) => {
      const k = key as keyof Profile;
      return updatedData[k] !== profile[k];
    });
  };

  // Context value
  const value: UserContextType = {
    user,
    isLoading,
    error: errorState,
    fetchUser: userActions.fetchUser,
    updateUser: userActions.updateUser,
    updateLanguage: userActions.updateLanguage,
    deleteAccount,
    hasUserChanges,
    userFormData,
    setUserFormData,
    profile,
    fetchProfile: profileActions.fetchProfile,
    updateProfile: profileActions.updateProfile,
    profileFormData,
    setProfileFormData,
    hasProfileChanges,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
});

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
